// O itinerário PROVISÓRIO do painel da viagem, derivado do recibo do cliente.
//
// Existe separado do `derive.ts` por uma razão de fonte: `derive.ts` projeta o
// `TripView` (vouchers do backend), e aqui a fonte é o CARRINHO que o navegador
// acabou de submeter. Misturar as duas no mesmo módulo é o primeiro passo para
// escrever um voucher sintético dentro do `TripView` — que é justamente o que este
// desenho não faz. Puro: `t()` + os helpers de `derive.ts`/`labels.ts`, sem estado.
import { cityByAirport, type TripView } from '@voll-intelligence/domain';
import { legLabel, routeLabel } from '@voll-intelligence/trip-document';
import {
  carrierDisplayName,
  type ChatUiFlightSegment,
  type CheckoutItem,
} from '@voll-intelligence/genui-contract';
import { t } from './i18n.js';
import type { BookingReceipt } from './receipt_types.js';
import { airlineBrandByIata, airlineLogoSvg, hasAirlineMark } from './airline_logos.js';
import { modalLabel, money } from './voucher_meta.js';
import {
  formatFlightDuration,
  canonicalModal,
  isDeadVoucherStatus,
  localHHMM,
  parseLocalIso,
  splitAirport,
  tripServices,
  type ModalKind,
} from './derive.js';
import { receiptModals } from './trip_cost.js';
import { cabinLabel, dayHeading, stopsLabel } from './labels.js';
import { documentCtx } from './document_ctx.js';

/** Uma linha provisória = UM trecho do recibo. Os papéis são FIXOS e não compactam
 *  quando falta um: quem não tem valor traz `null` e é a LINHA que decide o slot
 *  vazio, não o layout. Sem `voucherCode` e sem alvo de abertura de propósito — não
 *  existe localizador antes da emissão, e a ausência do chip é o que significa "sem
 *  documento" no vocabulário deste painel. */
export interface ProvisionalRow {
  key: string;
  /** "HH:MM" da partida na calha de 42px; '—' quando `departAt` não parseia — a
   *  mesma degradação da `StopRow` embarcada (`stop.time || '—'`). */
  time: string;
  name: string;
  /** O que vai DEPOIS do rótulo, pela régua do `stopTitle` (`labels.ts:218-234`): só o
   *  número quando existe logo curada (a marca substitui o nome), e a etiqueta da
   *  companhia junto do número quando não existe ("G3 3374") — senão o número fica
   *  órfão e a linha não diz quem opera o voo. */
  flightNo: string | null;
  /** A companhia SÓ quando há marca para desenhar; `null` caso contrário, e aí o nome
   *  dela já está no `flightNo`. É esse `null` que impede a linha de mostrar a marca e
   *  o nome ao mesmo tempo: o render desenha o `AirlineMark` exatamente quando este
   *  campo não é nulo. */
  carrier: string | null;
  /** O TÍTULO do card: a rota em códigos ('GRU → REC') no voo, hotel · quarto no
   *  hotel — a mesma anatomia da `StopRow` embarcada (paridade #2116). Escala e
   *  cabine desceram para o `qualifier`. */
  detail: string;
  /** As CIDADES da rota ('São Paulo → Recife'), quando as duas pontas são
   *  conhecidas (`cityByAirport`); nome de AEROPORTO não existe no client. `null`
   *  no hotel e em código desconhecido — a linha cai, nunca repete o código. */
  place: string | null;
  /** A terceira faixa: horários + duração no voo, "horário a confirmar" no hotel.
   *  `null` nos modais terrestres (carro, ônibus, mobilidade): a linha EMITIDA deles
   *  é só título + detalhe (`stopCardView` sem rodapé), e a provisória não pode ter
   *  uma faixa que some quando o voucher chega. */
  timeline: string | null;
  /** Formatado por `money`, e `null` na VOLTA de um bilhete de ida e volta: um item
   *  é um preço. */
  price: string | null;
  qualifier: string | null;
  /** O modal do `ModalIcon` da calha. Opcional e default `'FLIGHT'` por compatibilidade —
   *  toda linha existia só de voo até esta task; a linha de hotel é quem primeiro precisa
   *  dizer o próprio modal. */
  modal?: ModalKind;
}

/** Um dia do itinerário provisório. `heading` já é texto — o grupo sem data veste
 *  `trips.services.noDate`, a mesma palavra do itinerário real. */
export interface ProvisionalDay {
  key: string;
  heading: string;
  rows: ProvisionalRow[];
}

const NO_DATE = 'no-date';

/** A linha com a partida no wall-clock local: `at` só AGRUPA (a linha já carrega o
 *  texto da hora), e por isso não vaza no contrato. */
interface DatedRow {
  at: Date | null;
  row: ProvisionalRow;
}

interface RowSeed {
  key: string;
  seg: ChatUiFlightSegment;
  fareClass: string | null;
  leg: 'ida' | 'volta';
  price: string | null;
  qualifier: string | null;
}

/** Os pedaços presentes separados por ' · ', e `null` quando não sobra nenhum —
 *  slot vazio não nasce. */
function joinDot(parts: (string | null)[]): string | null {
  return parts.filter(Boolean).join(' · ') || null;
}

/** A chave do grupo: o dia do calendário LOCAL da partida. Não reusa
 *  `groupStopsByDay` porque aquela agrupa `TripStop` (vouchers), e é exatamente a
 *  fronteira que este módulo não atravessa. Exportada porque o `TripItinerary` junta
 *  os dias emitidos aos do recibo por ESTA chave — duas réguas de "mesmo dia"
 *  divergiriam no primeiro fuso. */
export function dayKey(at: Date | null): string {
  if (!at) return NO_DATE;
  const mes = String(at.getMonth() + 1).padStart(2, '0');
  const dia = String(at.getDate()).padStart(2, '0');
  return `${String(at.getFullYear())}-${mes}-${dia}`;
}

/** Ordem dos dias por `dayKey`: cronológica, e o grupo sem data no FIM — a mesma
 *  posição que ele tem no itinerário real. */
export function byDayKey(a: { key: string }, b: { key: string }): number {
  return Number(a.key === NO_DATE) - Number(b.key === NO_DATE) || a.key.localeCompare(b.key);
}

/** 'GRU → REC'. Meia flecha diz menos que nada: rota só com as duas pontas.
 *  Escala/cabine moram no `qualifier` (paridade #2116 — o título do card é a
 *  rota, como na `StopRow` embarcada). */
function rowRoute(seg: ChatUiFlightSegment): string {
  const from = splitAirport(seg.from).code;
  const to = splitAirport(seg.to).code;
  return from && to ? `${from} → ${to}` : '';
}

/** 'São Paulo → Recife', e `null` quando alguma ponta não resolve cidade — repetir
 *  o código na linha do lugar diria duas vezes a mesma coisa. */
function rowPlace(seg: ChatUiFlightSegment): string | null {
  const from = cityByAirport(splitAirport(seg.from).code);
  const to = cityByAirport(splitAirport(seg.to).code);
  return from && to ? `${from} → ${to}` : null;
}

/** '07:35 → 10:40 · 3h05', e "horário a confirmar" quando falta uma das pontas.
 *  `departAt`/`arriveAt` são strings LIVRES no fio (o provider de voo não garante
 *  ISO), e meia janela de horário é pior que dizer que não se sabe. */
function rowTimeline(seg: ChatUiFlightSegment): string {
  const depart = localHHMM(seg.departAt, null);
  const arrive = localHHMM(seg.arriveAt, null);
  if (!depart || !arrive) return t('trips.provisional.timeTbd');
  return joinDot([`${depart} → ${arrive}`, formatFlightDuration(seg.durationLabel)]) ?? '';
}

/** Companhia e número, pela MESMA régua do `stopTitle` (`labels.ts:218-234`): COM logo
 *  curada a marca substitui o nome e sobra o número (`number ?? mark`, porque um
 *  trecho sem número ainda precisa dizer quem o opera); SEM logo o nome entra JUNTO do
 *  número — é o que `carrierLabel` faz (`labels.ts:198-200`) —, senão "3374" fica órfão.
 *
 *  `seg.carrier` no fio é o CÓDIGO IATA, não a razão social (`flight_segment_schema.ts:
 *  23-32`) — `airlineLogoSvg` casa por NOME, então testá-la contra o código nunca acha
 *  marca nenhuma. Quem resolve a marca é `carrierDisplayName`, do genui-kit: uma régua
 *  só para todo consumidor do segmento, porque o schema permite `carrierName: ''` (sem
 *  `.min(1)`) e um `??` local deixaria a string vazia vencer, apagando a marca sem cair
 *  para o código. Resolvida ANTES do teste.
 *
 *  A decisão mora aqui, e não no render, por duas razões: a régua é uma só para a linha
 *  emitida e a provisória (duas cópias divergem no primeiro ajuste), e devolver
 *  `carrier: null` no ramo sem marca torna impossível o estado em que a marca e o nome
 *  da companhia aparecem os dois na mesma linha. `airlineLogoSvg` casa pela PRIMEIRA
 *  PALAVRA e só conhece LATAM/GOL/AZUL; as demais marcam pelo IATA (`hasAirlineMark`). */
function carrierAndNumber(seg: ChatUiFlightSegment): {
  carrier: string | null;
  flightNo: string | null;
} {
  const mark = carrierDisplayName(seg) || null;
  const number = seg.flightNumber.trim() || null;
  if (mark && airlineLogoSvg(mark)) return { carrier: mark, flightNo: number ?? mark };
  // Fio sem nome (só o código IATA — o caso comum sem enriquecimento): a marca
  // ainda é determinística para as três curadas ('LA'/'G3'/'AD'). Sem este
  // fallback o thumb do card degradava pro avião genérico exatamente no caso
  // dominante (#2116).
  const brand = airlineBrandByIata(seg.carrier);
  if (brand) return { carrier: brand, flightNo: number ?? brand };
  // Fora das três a marca é a do IATA (`AirlineLogo` no thumb, chip do código quando a
  // imagem não carrega): pelo nome quando ele se traduz, senão pelo código do fio (#5653).
  if (mark && hasAirlineMark(mark)) return { carrier: mark, flightNo: number ?? mark };
  const code = seg.carrier.trim();
  if (hasAirlineMark(code)) return { carrier: code, flightNo: number ?? code };
  return { carrier: null, flightNo: [mark, number].filter(Boolean).join(' ') || null };
}

function datedRow(seed: RowSeed): DatedRow {
  const { seg } = seed;
  const { carrier, flightNo } = carrierAndNumber(seg);
  return {
    at: parseLocalIso(seg.departAt),
    row: {
      key: seed.key,
      time: localHHMM(seg.departAt, null) || '—',
      name: t(seed.leg === 'volta' ? 'trips.agenda.flightBack' : 'trips.agenda.flightOut'),
      flightNo,
      carrier,
      detail: rowRoute(seg),
      place: rowPlace(seg),
      timeline: rowTimeline(seg),
      price: seed.price,
      // Escala (por PERNA — a volta pode ter parada que a ida não tem) e cabine
      // na frente do qualificador do bilhete: 'direto · Econômica · ida e volta'.
      qualifier: joinDot([
        stopsLabel(seg.connections?.length),
        cabinLabel(seed.fareClass),
        seed.qualifier,
      ]),
    },
  };
}

/** Data-só (`YYYY-MM-DD`) como `Date` LOCAL — nunca `new Date(string)`: aquele construtor
 *  lê a string à meia-noite UTC, e em `America/Sao_Paulo` (fuso negativo) isso imprime o
 *  dia ANTERIOR. `checkIn`/`checkOut` do item de hotel chegam nesse formato (documentado
 *  em `HotelItemDetailSchema`), e é o MESMO bug que `ui/booking/BancadaOutcome.tsx`
 *  (`dayLabel`) já corrigiu para o mesmo formato — reescrito aqui porque aquele helper é
 *  privado ao módulo dele e este arquivo não pode importar dele (fronteira de dono do PR).
 *  `null` para string ausente ou fora do formato — nunca um `Date` inválido silencioso. */
export function dateOnlyLocal(value: string | undefined): Date | null {
  const m = value ? /^(\d{4})-(\d{2})-(\d{2})$/.exec(value) : null;
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

/** As duas linhas de UM item de hotel: check-in sempre PRIMEIRO (com o valor), check-out
 *  quando a fonte informou saída — mesma forma do voo (ida com valor, volta sem), mas os
 *  papéis são check-in/check-out e não há segmento: o nome do hotel/quarto substitui a
 *  rota (`rowDetail`), e não existe HH:MM nenhum na fonte — `time` fica '—', a mesma
 *  degradação da `StopRow` embarcada, e `timeline` reusa a frase de horário indisponível
 *  do voo (`trips.provisional.timeTbd`) em vez de repetir a data, que já é o CABEÇALHO do
 *  grupo (`dayKey`/`dayHeading`, calculados a partir do mesmo `checkIn`/`checkOut`). */
function hotelRows(
  item: Extract<CheckoutItem, { productType: 'hotel' }>,
  index: number,
): DatedRow[] {
  const { hotelName, roomName, checkIn, checkOut } = item.detail;
  const detail = joinDot([hotelName ?? null, roomName ?? null]) ?? '';
  const price = money(item.price.amount, item.price.currency);
  const seed = `${String(index)}-${item.offerId}`;
  const checkInRow: DatedRow = {
    at: dateOnlyLocal(checkIn),
    row: {
      key: `${seed}-0`,
      time: '—',
      name: t('trips.agenda.checkIn'),
      flightNo: null,
      carrier: null,
      detail,
      place: null,
      timeline: t('trips.provisional.timeTbd'),
      price,
      qualifier: null,
      modal: 'HOTEL',
    },
  };
  if (!checkOut) return [checkInRow];
  return [
    checkInRow,
    {
      at: dateOnlyLocal(checkOut),
      row: {
        key: `${seed}-1`,
        time: '—',
        name: t('trips.agenda.checkOut'),
        flightNo: null,
        carrier: null,
        detail,
        place: null,
        timeline: t('trips.provisional.timeTbd'),
        price: null,
        qualifier: null,
        modal: 'HOTEL',
      },
    },
  ];
}

// ── Modais terrestres (#5359) ────────────────────────────────────────────────
// Cada linha imita a linha EMITIDA do mesmo modal — `stopCardView` sem rodapé:
// título (`stopTitle`) e detalhe (`stopDetail`/`serviceSubtitle`), na régua de
// `trip-document/labels.ts`. Assim a troca pelo voucher não muda o desenho. A
// terceira faixa só existe quando o recibo sabe algo que a pessoa precisa antes do
// documento (as poltronas do ônibus, o veículo do carro com motorista).

type ItemOf<P extends CheckoutItem['productType']> = Extract<CheckoutItem, { productType: P }>;

/** Uma linha terrestre: sem marca de companhia nem número de voo, e sem a faixa de
 *  horários (o HH:MM da partida já está na calha). */
function groundRow(
  fields: Pick<ProvisionalRow, 'key' | 'name' | 'detail' | 'place' | 'price' | 'qualifier'> & {
    modal: ModalKind;
    startsAt: string | undefined;
  },
): DatedRow {
  const { startsAt, ...row } = fields;
  return {
    at: parseLocalIso(startsAt),
    row: {
      ...row,
      time: localHHMM(startsAt, null) || '—',
      flightNo: null,
      carrier: null,
      timeline: null,
    },
  };
}

/** O carro numa linha só, como o voucher de locação (um passo sem perna): o modelo,
 *  senão a locadora (`serviceHeading`), e a janela "Retirada 10:00 → Devolução 12:00"
 *  (`serviceSubtitle`) no detalhe. O dia é o da retirada. */
function rentalRows(item: ItemOf<'rental'>, index: number): DatedRow[] {
  const { vehicleName, rentalCompany, pickupAt, dropoffAt } = item.detail;
  const pickupTime = localHHMM(pickupAt, null);
  const dropoffTime = localHHMM(dropoffAt, null);
  const window = [
    pickupTime && `${t('trips.services.pickup')} ${pickupTime}`,
    dropoffTime && `${t('trips.services.dropoff')} ${dropoffTime}`,
  ]
    .filter(Boolean)
    .join(' → ');
  return [
    groundRow({
      key: `${String(index)}-${item.offerId}-0`,
      modal: 'RENTAL',
      startsAt: pickupAt,
      name: modalLabel('RENTAL'),
      detail: vehicleName || rentalCompany,
      place: window || null,
      price: money(item.price.amount, item.price.currency),
      qualifier: null,
    }),
  ];
}

/** "Poltronas 12, 13 · 5" — as escolhidas em cada trecho da viagem, na ordem dela.
 *  `null` quando nenhuma foi escolhida (`[]` = a equipe VOLL escolhe): dizer "sem
 *  poltrona" leria como falta. */
function seatsLabel(seats: string[][] | undefined): string | null {
  const picked = (seats ?? []).filter((segment) => segment.length > 0);
  const count = picked.reduce((sum, segment) => sum + segment.length, 0);
  if (count === 0) return null;
  return t('trips.provisional.seats', {
    count,
    seats: picked.map((segment) => segment.join(', ')).join(' · '),
  });
}

/** Um trecho de ônibus por item (o carrinho de ida e volta já chega com um item por
 *  perna), no formato da perna emitida: "Ônibus · Ida · Cometa" no título
 *  (`stopTitle`: modal, perna, empresa) e a rota pela parte-cidade das rodoviárias
 *  (`routeLabel`, a mesma régua que lê `departureStation`). Item sem `leg` não ganha
 *  "Ida": a fonte também só nomeia a perna que marcou. Poltronas na terceira faixa. */
function busRows(item: ItemOf<'bus'>, index: number): DatedRow[] {
  const { departure, arrival, carrierName, leg, seats } = item.detail;
  const legName = leg ? legLabel({ leg: leg === 'return' ? 'volta' : 'ida' }, documentCtx()) : null;
  const name = joinDot([modalLabel('BUS'), legName]) ?? '';
  return [
    groundRow({
      key: `${String(index)}-${item.offerId}-0`,
      modal: 'BUS',
      startsAt: departure.dateTime,
      name,
      detail: joinDot([name, carrierName ?? null]) ?? name,
      place: routeLabel({ from: departure.stopName, to: arrival.stopName }),
      price: money(item.price.amount, item.price.currency),
      qualifier: seatsLabel(seats),
    }),
  ];
}

/** Uma linha por corrida (ida e, quando há, volta), como o voucher de mobilidade: o
 *  fornecedor no título (`serviceHeading`) e o trajeto "origem → destino" no detalhe
 *  (`serviceSubtitle`) — só a origem no carro com motorista, que não tem destino. O
 *  valor é do ITEM e mora na primeira corrida, a mesma régua do bilhete do voo. */
function mobilityRows(item: ItemOf<'mobility'>, index: number): DatedRow[] {
  const { supplierName, outward, return: back, vehicle } = item.detail;
  const rides = back ? [outward, back] : [outward];
  return rides.map((ride, leg) =>
    groundRow({
      key: `${String(index)}-${item.offerId}-${String(leg)}`,
      modal: 'MOBILITY',
      startsAt: ride.scheduleDate,
      name: modalLabel('MOBILITY'),
      detail: supplierName,
      place: ride.to ? `${ride.from} → ${ride.to}` : ride.from,
      price: leg === 0 ? money(item.price.amount, item.price.currency) : null,
      qualifier: joinDot([
        vehicle ?? null,
        back && leg === 0 ? t('trips.provisional.roundTrip') : null,
      ]),
    }),
  );
}

/** As linhas de UM item: a ida sempre, a volta quando o bilhete tem volta.
 *
 *  O VALOR fica na ida com o qualificador "ida e volta", e a volta vem sem valor
 *  dizendo "mesmo bilhete da ida" — a unidade de preço é o BILHETE, não o trecho
 *  ("1 item = one-way ou ida-e-volta", `data/flight_booking_api.ts:37-42`).
 *  "por passageiro" entra SÓ com 2+ viajantes e SÓ na linha que tem valor: com um
 *  viajante o valor por assento já é o total, e rotular convida à dúvida.
 *
 *  Os demais modais têm ramo próprio (`hotelRows`, Task 9; carro, ônibus e mobilidade,
 *  #5359): a união `CheckoutItem` é discriminada e o `detail` de cada membro tem outra
 *  forma. */
function itemRows(item: CheckoutItem, index: number, travelers: number): DatedRow[] {
  switch (item.productType) {
    case 'hotel':
      return hotelRows(item, index);
    case 'rental':
      return rentalRows(item, index);
    case 'bus':
      return busRows(item, index);
    case 'mobility':
      return mobilityRows(item, index);
    case 'flight':
      return flightRows(item, index, travelers);
    default: {
      const exhaustive: never = item;
      return exhaustive;
    }
  }
}

function flightRows(item: ItemOf<'flight'>, index: number, travelers: number): DatedRow[] {
  const fareClass = item.detail.fareClass ?? null;
  const back = item.detail.return;
  const perTraveler = travelers >= 2 ? t('trips.provisional.perTraveler') : null;
  // A chave carrega a POSIÇÃO do item além do `offerId`: o par split do ida-e-volta
  // devolve ids de matriz, e dois itens com o mesmo `offerId` colapsariam duas linhas
  // numa só na lista React.
  const seed = `${String(index)}-${item.offerId}`;
  const outbound = datedRow({
    key: `${seed}-0`,
    seg: item.detail.outbound,
    fareClass,
    leg: 'ida',
    price: money(item.price.amount, item.price.currency),
    qualifier: joinDot([back ? t('trips.provisional.roundTrip') : null, perTraveler]),
  });
  if (!back) return [outbound];
  return [
    outbound,
    datedRow({
      key: `${seed}-1`,
      seg: back,
      fareClass,
      leg: 'volta',
      price: null,
      qualifier: t('trips.provisional.sameTicket'),
    }),
  ];
}

/** Cronológico pela partida no wall-clock local — mesma régua do `byStartAsc` do
 *  itinerário emitido (`derive.ts:819-822`). O grupo sem data já foi separado por
 *  `dayKey` antes deste comparador rodar, mas o tipo de `DatedRow.at` continua
 *  `Date | null`, e o comparador não finge o contrário. */
function byDepartureAsc(a: DatedRow, b: DatedRow): number {
  return (a.at?.getTime() ?? Infinity) - (b.at?.getTime() ?? Infinity);
}

/** Um dia em construção: as linhas ficam com o `at` vivo enquanto o grupo cresce, para
 *  ordenar por horário de partida só depois de o grupo estar completo. */
interface DayGroup {
  key: string;
  heading: string;
  entries: DatedRow[];
}

/** Os trechos do recibo agrupados por dia em ordem CRONOLÓGICA — mesma régua do
 *  itinerário real, que ordena nos dois níveis (`derive.ts:1007`/`:1074`, comparador em
 *  `:819-822`) — e com o grupo sem data no FIM, a mesma posição que ele tem lá. A ordem
 *  do carrinho (ida → volta, trecho 1 → N) só sobrevive DENTRO de um mesmo instante:
 *  dois trechos com a mesma partida mantêm a ordem em que chegaram, porque `Array#sort`
 *  é estável. */
export function provisionalDays(receipt: BookingReceipt): ProvisionalDay[] {
  const travelers = receipt.travelers.length;
  const byDay = new Map<string, DayGroup>();
  for (const entry of receipt.items.flatMap((item, index) => itemRows(item, index, travelers))) {
    const key = dayKey(entry.at);
    const day = byDay.get(key);
    if (day) {
      day.entries.push(entry);
      continue;
    }
    byDay.set(key, {
      key,
      heading: entry.at ? dayHeading(entry.at) : t('trips.services.noDate'),
      entries: [entry],
    });
  }
  return [...byDay.values()].sort(byDayKey).map((day) => ({
    key: day.key,
    heading: day.heading,
    rows: [...day.entries].sort(byDepartureAsc).map((entry) => entry.row),
  }));
}

/** Quantos ITENS do recibo o painel de fato desenha como linha — não
 *  `receipt.items.length` cru. Hoje todo modal tem ramo em `itemRows` (#5359), mas a
 *  contagem chama o MESMO `itemRows` que `provisionalDays` usa para desenhar, e não
 *  uma segunda régua "quais productType contam": um item que não vire linha não pode
 *  fazer a mala anunciar o que o painel não mostra (achado do review, #2205). */
export function provisionalItemCount(receipt: BookingReceipt): number {
  const travelers = receipt.travelers.length;
  return receipt.items.filter((item, index) => itemRows(item, index, travelers).length > 0).length;
}

/** A viagem já tem serviço VIVO do modal pedido? É a condição de saída do bloco
 *  provisório, avaliada no render: quando o voucher daquele modal chega, o itinerário
 *  real assume. Generalizada na Task 9 (era `hasLiveFlightService`, cravada em `FLIGHT`)
 *  para o painel poder chamar com o modal do PRÓPRIO recibo — um recibo de hotel não
 *  pode mais fechar (nem ser fechado por) um voo, e vice-versa.
 *
 *  Presença de SERVIÇO, e não de segmento: `tripStops` empurra um passo mesmo quando
 *  o voucher chega sem enriquecimento (`legs.length === 0`), então "esperar segmentos"
 *  deixaria o mesmo serviço em dois blocos por uma janela inteira. Morto
 *  (cancelado/recusado/expirado) não conta — o que morreu não promete nada.
 *
 *  LIMITAÇÃO V1 (decisão de escopo, não bug): a saída é por MODAL, não por identidade.
 *  Numa trip que JÁ tem voucher HOTEL vivo, um recibo de hotel NOVO (outra reserva,
 *  outro `serviceRequestId`) fecha esta janela no primeiro render e o provisório some
 *  até o voucher da reserva nova chegar — a mesma heurística que hoje já vale para
 *  voo. Limpeza por identidade (via `correlationId`, que hotel e voo carregam; o recibo
 *  já guarda `serviceRequestId` para isso) é v2. */
export function hasLiveServiceOfModal(trip: TripView, modal: ModalKind): boolean {
  return tripServices(trip).some(
    (service) => service.modal === modal && !isDeadVoucherStatus(service.status),
  );
}

/** Os modais que a viagem JÁ TEM — o conjunto que filtra as sugestões do painel
 *  (chips do trilho e frases do composer, #2186). Três fontes:
 *
 *   · voucher VIVO do modal — o write-ahead reconcilia IN_PROGRESS, que é vivo,
 *     então a reserva em andamento já conta;
 *   · modal declarado em `trip.modals` SEM voucher NENHUM do modal — a janela
 *     write-ahead em que a ordem existe e o voucher ainda não materializou (é o
 *     mesmo estado que `tripModals` lê pelo fallback dele). Um voucher MORTO
 *     vence a declaração: o cancelado se oferece de novo;
 *   · o recibo provisório da bancada (`receiptModals` — TODOS os itens, não só o
 *     primeiro) — o que o navegador acabou de submeter é serviço presente antes
 *     de qualquer voucher existir. */
export function presentModals(trip: TripView, receipt?: BookingReceipt | null): Set<ModalKind> {
  const present = new Set<ModalKind>();
  const voucherModals = new Set<ModalKind>();
  for (const service of tripServices(trip)) {
    voucherModals.add(service.modal);
    if (!isDeadVoucherStatus(service.status)) present.add(service.modal);
  }
  for (const declared of trip.modals) {
    const kind = canonicalModal(declared);
    if (kind && !voucherModals.has(kind)) present.add(kind);
  }
  if (receipt) for (const reserved of receiptModals(receipt)) present.add(reserved);
  return present;
}
